import { useState } from "react";
import {
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  type Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";

export interface UsePopoverOptions {
  /** Anchor placement. Forwarded to floating-ui. */
  placement: Placement;
  /** Gap in pixels between the trigger and the popover. Defaults to 8. */
  offset?: number;
  /**
   * If true, the popover also opens on hover using floating-ui's `safePolygon`
   * (cursor can move diagonally toward the popover without closing).
   * Default false — click-only.
   */
  hover?: boolean;
  /** Open/close delays in ms for the hover interaction. */
  hoverDelay?: { open: number; close: number };
}

/**
 * Wraps the boilerplate for a click-triggered floating-ui popover (open
 * state, positioning with flip/shift, click-to-open, click-outside dismissal,
 * escape-to-close, menu ARIA role). Optionally adds a hover interaction with
 * the safePolygon close-handler for nested-menu UX.
 *
 * Extracted because the SettingsPopover trigger, its nested Guides submenu,
 * and the TimeDisplay format chevron all reproduced the same setup verbatim.
 */
export function usePopover(options: UsePopoverOptions) {
  const {
    placement,
    offset: offsetValue = 8,
    hover: hoverEnabled = false,
    hoverDelay = { open: 100, close: 150 },
  } = options;

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [offsetMiddleware(offsetValue), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  // useHover always called (rules of hooks); `enabled` toggles its effect.
  const hover = useHover(context, {
    enabled: hoverEnabled,
    delay: hoverDelay,
    handleClose: hoverEnabled ? safePolygon({ buffer: 4 }) : null,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
    role,
  ]);

  return {
    isOpen,
    setIsOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
  };
}
